import { useEffect, useState } from "react";

import { Alert, Typography } from "antd";
import { DEPTH_MISSING_WARNING } from "constants/scenario.constants";
import styled from "styled-components";

import GeometryBinSelector from "./components/GeometryBinSelector";
import RingFenceTable from "./components/RingFenceTable";
import { usePadScenario } from "./contexts/usePadScenario";
import { useRingFenceController } from "./hooks/ring-fence/useRingFenceController";
import { sticksHaveDepth } from "./utils/scenarioHelpers";

const { Title } = Typography;

interface RingFenceTabProps {
  containerHeight: number;
}

export default function RingFenceTab({ containerHeight }: RingFenceTabProps) {
  const {
    state: { sticks }
  } = usePadScenario();
  const [missingDepth, setMissingDepth] = useState(false);

  useEffect(() => {
    if (sticks.length > 0) {
      setMissingDepth(!sticksHaveDepth(sticks));
    } else {
      setMissingDepth(false);
    }
  }, [sticks]);

  const {
    // State
    scenarioId,
    selectedGeometryBin,
    setSelectedGeometryBin,
    selectedPolygon,
    setSelectedPolygon,
    tableData,

    // Data
    allGeomBins,
    typeWells,
    isLoadingRingFences,
    isLoadingGeomBin,
    isLoadingTypeWells,

    // Mutation states
    isCreating,
    isUpdating,
    isDeleting,

    // Handlers
    handleAddRingFence,
    handleSave,
    handleCancel,
    handleEdit,
    handleDelete,
    handleFieldChange,

    // Helper functions
    getGeomBinNodes,
    getGeomBinNodeName,
    getTypeWellName
  } = useRingFenceController();

  if (!scenarioId) {
    return (
      <Container>
        <Title level={4}>No scenario selected</Title>
      </Container>
    );
  }

  const isAddDisabled =
    !selectedPolygon || tableData.some((row) => row.isEditing) || isLoadingGeomBin;

  return (
    <Container>
      <GeometryBinSelector
        selectedGeometryBin={selectedGeometryBin}
        setSelectedGeometryBin={setSelectedGeometryBin}
        selectedPolygon={selectedPolygon}
        setSelectedPolygon={setSelectedPolygon}
        allGeomBins={allGeomBins}
        geomBinNodes={getGeomBinNodes(false)}
        isLoadingGeomBin={isLoadingGeomBin}
        onAddRingFence={handleAddRingFence}
        isAddDisabled={isAddDisabled}
        isCreating={isCreating}
      />
      {missingDepth && (
        <Alert
          message={DEPTH_MISSING_WARNING}
          type="warning"
          showIcon
          style={{ margin: "8px 0" }}
        />
      )}
      <RingFenceTable
        tableData={tableData}
        containerHeight={containerHeight}
        selectedGeometryBin={selectedGeometryBin}
        selectedPolygon={selectedPolygon}
        isLoadingRingFences={isLoadingRingFences}
        isLoadingGeomBin={isLoadingGeomBin}
        isLoadingTypeWells={isLoadingTypeWells}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        geomBinNodes={getGeomBinNodes()}
        typeWells={typeWells}
        onSave={handleSave}
        onCancel={handleCancel}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onFieldChange={handleFieldChange}
        getGeomBinNodeName={getGeomBinNodeName}
        getTypeWellName={getTypeWellName}
      />
    </Container>
  );
}

const Container = styled.div`
  padding: 10px;
  height: 100%;
  max-height: 100%;
  display: grid;
  grid-template-rows: minmax(0, auto) minmax(0, auto) minmax(0, 1fr);
  overflow-y: auto;
`;
